import React, { useState, useEffect, lazy, Suspense } from "react";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useProfileInfo } from "../../contexts/ProfileInfoContext";
import { useProfileViews } from "../../hooks/useProfileViews";
import { sendTeamInviteEmail } from "../../services/emailService";
import useListingCredits from "../../hooks/useListingCredits";
import { getTeamSeatLimit } from "../../constants/teamSeats";
import { formatTimeAgo } from "../../components/employer/dashboard/constants";
import LottieLoader from "../../components/LottieLoader";

ChartJS.register(ArcElement, Tooltip, Legend);

// Lazy load dashboard components for better performance
const DashboardStats = lazy(() => import("../../components/employer/dashboard/DashboardStats"));
const YourJobsSection = lazy(() => import("../../components/employer/dashboard/YourJobsSection"));
const TeamMembersCard = lazy(() => import("../../components/employer/dashboard/TeamMembersCard"));
const TeamMemberModal = lazy(() => import("../../components/employer/dashboard/TeamMemberModal"));

// TODO: Refactor this component 
const EmployerDashboard = () => {
    const { user } = useAuth();
    const { profileInfo } = useProfileInfo();
    const { getProfileViewStats } = useProfileViews();
    const { credits, loading: creditsLoading } = useListingCredits();
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState("All");
    const [groupExpanded, setGroupExpanded] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortOrder, setSortOrder] = useState("newest"); // "newest" or "oldest"
    const [showSortMenu, setShowSortMenu] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [jobsLoading, setJobsLoading] = useState(true);
    const [jobs, setJobs] = useState([]);
    const [jobsError, setJobsError] = useState(null);
    const [dynamicJobApplications, setDynamicJobApplications] = useState([]);
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [profileAnalytics, setProfileAnalytics] = useState({
        totalViews: 0,
        totalClicks: 0,
        recentViews: 0,
        recentClicks: 0
    });
    const [summaryStats, setSummaryStats] = useState({
        totalJobsListed: 0,
        candidateMatches: 0,
        acceptedApplicants: 0,
        rejectedApplicants: 0,
        hires: 0,
        candidateMatchesChange: 0,
        acceptedApplicantsChange: 0,
        rejectedApplicantsChange: 0,
        hiresChange: 0,
        candidateMatchesLastWeek: 0,
        acceptedApplicantsLastWeek: 0,
        rejectedApplicantsLastWeek: 0,
        hiresLastWeek: 0,
        jobMatchPercentage: 0,
        jobMatchChange: 0
    });
    const [statsLoading, setStatsLoading] = useState(true);
    const [showTeamMemberModal, setShowTeamMemberModal] = useState(false);
    const [teamMemberForm, setTeamMemberForm] = useState({
        firstName: "",
        lastName: "",
        workEmail: "",
        role: "member"
    });
    const [editingMemberId, setEditingMemberId] = useState(null);
    const [savingTeamMember, setSavingTeamMember] = useState(false);


    // Calculate team seat limits
    const metadataPlanLabel = user?.user_metadata?.subscription_plan || "Free Starter";
    const creditsOrMetadata = credits ?? { isFreePlan: metadataPlanLabel === "Free Starter" };
    const currentPlanLabel = creditsOrMetadata.isFreePlan ? "Free Starter" : "Sponsored Jobs";
    const teamSeatLimit = getTeamSeatLimit(creditsOrMetadata);
    const currentTeamCount = teamMembers.length;
    const hasReachedSeatLimit = currentTeamCount >= teamSeatLimit;

    // Fetch team members from database
    const fetchTeamMembers = async () => {
        if (!user) return;
        
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('team_members')
                .select(`
                    *,
                    users_profiles(profile_image_url, firstname, lastname)
                `)
                .eq('employer_id', user.id)
                .order('created_at', { ascending: false });

            if (error) {
                return;
            }

            // Transform data to match the expected format
            const transformedMembers = data.map(member => {
                const userProfile = Array.isArray(member.users_profiles) 
                    ? member.users_profiles[0] 
                    : member.users_profiles;
                
                return {
                    id: member.id,
                    name: `${member.first_name} ${member.last_name}`.trim(),
                    role: member.roles && member.roles.length > 0 ? member.roles[0] : 'Team Member',
                    avatar: member.first_name ? member.first_name.charAt(0).toUpperCase() : 'T',
                    email: member.work_email,
                    allRoles: member.roles || [],
                    userProfileId: member.user_profile_id,
                    profileImageUrl: userProfile?.profile_image_url,
                    profileName: `${userProfile?.firstname || ''} ${userProfile?.lastname || ''}`.trim()
                };
            });

            setTeamMembers(transformedMembers);
        } catch (error) {
            // Error fetching team members
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeamMembers();
        fetchJobListings();
        fetchProfileAnalytics();
        fetchSummaryStats();
        syncCompanyInformation();
    }, [user]);

    // Sync company information across all job listings
    const syncCompanyInformation = async () => {
        if (!user?.user_metadata?.employer_profile?.companyInfo) return;
        
        try {
            const companyInfo = user.user_metadata.employer_profile.companyInfo;
            
            const { error: updateError } = await supabase
                .from('internal_job_listings')
                .update({ 
                    company_name: companyInfo.companyName,
                    company_url: companyInfo.companyUrl,
                    industry: companyInfo.industry,
                    company_description: companyInfo.about,
                    logo_filename: companyInfo.logoFileName,
                    contact_first_name: companyInfo.firstName,
                    contact_last_name: companyInfo.lastName,
                    contact_email: companyInfo.workEmail
                })
                .eq('employer_id', user.id);
            
            if (updateError) {
                // Error syncing company information
            }
        } catch (error) {
            // Error in syncCompanyInformation
        }
    };

    // Fetch job listings from database
    const fetchJobListings = async () => {
        if (!user) return;
        let jobs = [];
        
        try {
            setJobsLoading(true);
            if (user?.email.includes('@fourminutemedicine.com')) {
                try {
                    jobs = await fetchAllIncludingDevModeJobs();
                } catch (error) {
                    console.error("Error fetching dev mode jobs:", error);
                    throw error;
                }
            } else {
                try {
                    jobs = await fetchAllExcludingDevModeJobs();
                } catch (error) {
                    console.error("Error fetching non-dev mode jobs:", error);
                    throw error;
                }
            }

            if (jobsError) {
                return;
            }

            // Fetch all applications for all jobs in parallel (fixes N+1 query problem)
            const jobIds = (jobs || []).map(job => job.id);
            
            // Fetch all applications for all jobs at once
            const { data: allApplications, error: allAppsError } = await supabase
                .from('applications')
                .select('id, status, user_id, job_listing_id')
                .in('job_listing_id', jobIds);

            if (allAppsError) {
                // Error fetching applications
            }

            // Group applications by job_listing_id
            const applicationsByJob = {};
            (allApplications || []).forEach(app => {
                if (!applicationsByJob[app.job_listing_id]) {
                    applicationsByJob[app.job_listing_id] = [];
                }
                applicationsByJob[app.job_listing_id].push(app);
            });

            // Collect all unique user IDs from all applications
            const allUserIds = [...new Set((allApplications || []).map(app => app.user_id).filter(Boolean))];

            // Batch fetch all candidate profiles in a single query (fixes N+1 problem)
            const profilesMap = {};
            if (allUserIds.length > 0) {
                const { data: allProfiles, error: profilesError } = await supabase
                    .from('users_profiles')
                    .select('profileid, profile_image_url, firstname, lastname')
                    .in('profileid', allUserIds);

                if (!profilesError && allProfiles) {
                    allProfiles.forEach(profile => {
                        let profileImageUrl = null;
                        if (profile.profile_image_url) {
                            if (profile.profile_image_url.startsWith('http')) {
                                profileImageUrl = profile.profile_image_url;
                            } else {
                                const { data: imageData } = supabase.storage
                                    .from('profile-images')
                                    .getPublicUrl(profile.profile_image_url);
                                profileImageUrl = imageData.publicUrl;
                            }
                        }
                        profilesMap[profile.profileid] = {
                            imageUrl: profileImageUrl,
                            initials: profile.firstname && profile.lastname 
                                ? `${profile.firstname.charAt(0)}${profile.lastname.charAt(0)}`.toUpperCase()
                                : 'U'
                        };
                    });
                }
            }

            // Process each job with pre-fetched data
            const jobsWithApplications = (jobs || []).map((job) => {
                const jobApplications = applicationsByJob[job.id] || [];
                const previewApplications = jobApplications.slice(0, 4);

                // Build candidate images from first 4 applicants (avatars only)
                const candidateImages = previewApplications
                    .map((app) => profilesMap[app.user_id])
                    .filter(Boolean);

                return {
                    ...job,
                    candidates: jobApplications.length,
                    applications: previewApplications,
                    candidateImages,
                };
            });

            // Transform to match the expected format
            const transformedJobs = jobsWithApplications.map(job => ({
                id: job.id,
                title: job.title,
                company: job.company_name,
                status: job.status === 'published' ? 'Posted' : job.status === 'draft' ? 'Draft' : 'Archived',
                type: job.employment_type,
                candidates: job.candidates,
                candidateImages: job.candidateImages || [],
                location: job.location,
                published: formatTimeAgo(job.created_at),
                created_at: job.created_at, // Keep created_at for sorting
                companyLogo: job.company_name ? job.company_name.charAt(0).toUpperCase() : 'C',
                specialty: job.specialty,
                workPreference: job.work_preference,
                salaryRange: job.salary_range,
                experienceLevel: job.experience_level
            }));

            setDynamicJobApplications(transformedJobs);
        } catch (error) {
            // Error fetching job listings
        } finally {
            setJobsLoading(false);
        }
    };

    const fetchAllIncludingDevModeJobs = async () => {
        const { data: jobs, error: jobsError } = await supabase
            .from('internal_job_listings')
            .select('*')
            .eq('employer_id', user.id)
            .order('created_at', { ascending: false });
        return jobs;
    };

    const fetchAllExcludingDevModeJobs = async () => {
        const { data: jobs, error: jobsError } = await supabase
            .from('internal_job_listings')
            .select('*')
            .eq('employer_id', user.id)
            .eq('is_dev_job', false)
            .order('created_at', { ascending: false });
        return jobs;
    };

    const fetchProfileAnalytics = async () => {
        // Temporarily disabled - set to 0 until fixed
        setProfileAnalytics({
            totalViews: 0,
            totalClicks: 0,
            recentViews: 0,
            recentClicks: 0
        });
        
        // Original code commented out until profile views are fixed
        /*
        if (!user?.id) {
            setProfileAnalytics({
                totalViews: 0,
                totalClicks: 0,
                recentViews: 0,
                recentClicks: 0
            });
            return;
        }
        
        try {
            const stats = await getProfileViewStats(user.id);
            
            // Always update state, even if stats is null
            if (stats) {
                // Calculate recent views (past 7 days)
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                
                const recentViews = stats.recentViews?.filter(view => 
                    new Date(view.view_timestamp) >= sevenDaysAgo
                ).length || 0;

                setProfileAnalytics({
                    totalViews: stats.totalViews || 0,
                    totalClicks: stats.totalViews || 0, // Same as profile views
                    recentViews: recentViews,
                    recentClicks: recentViews // Same as recent views
                });
            } else {
                // If stats is null, reset to 0
                setProfileAnalytics({
                    totalViews: 0,
                    totalClicks: 0,
                    recentViews: 0,
                    recentClicks: 0
                });
            }
        } catch (error) {
            console.error('Error fetching profile analytics:', error);
            // Reset to 0 on error
            setProfileAnalytics({
                totalViews: 0,
                totalClicks: 0,
                recentViews: 0,
                recentClicks: 0
            });
        }
        */
    };

    /** Job ids used for application totals — aligned with EnhancedHiringPipeline / Candidates */
    const fetchEmployerJobIdsForApplications = async () => {
        let query = supabase
            .from('internal_job_listings')
            .select('id')
            .eq('employer_id', user.id)
            .neq('status', 'closed');

        if (!user.email?.includes('@fourminutemedicine.com')) {
            query = query.eq('is_dev_job', false);
        }

        const { data: jobs, error: jobsError } = await query;
        if (jobsError) throw jobsError;
        return (jobs || []).map((j) => j.id);
    };

    const fetchSummaryStats = async () => {
        if (!user?.id) return;
        
        try {
            setStatsLoading(true);

            const jobIds = await fetchEmployerJobIdsForApplications();

            const emptyStats = {
                totalJobsListed: 0,
                candidateMatches: 0,
                acceptedApplicants: 0,
                rejectedApplicants: 0,
                hires: 0,
                candidateMatchesChange: 0,
                acceptedApplicantsChange: 0,
                rejectedApplicantsChange: 0,
                hiresChange: 0,
                candidateMatchesLastWeek: 0,
                acceptedApplicantsLastWeek: 0,
                rejectedApplicantsLastWeek: 0,
                hiresLastWeek: 0,
                jobMatchPercentage: 0,
                jobMatchChange: 0,
            };

            if (jobIds.length === 0) {
                setSummaryStats(emptyStats);
                setStatsLoading(false);
                return;
            }

            // Same application query as EnhancedHiringPipeline fetchApps (all rows, any status)
            const { data: allApplications, error: appsError } = await supabase
                .from('applications')
                .select('*')
                .in('job_listing_id', jobIds)
                .order('application_date', { ascending: false });

            if (appsError) throw appsError;

            const applications = allApplications || [];

            const totalJobsListed = jobIds.length;
            const candidateMatches = applications.length;
            
            // Calculate Shortlist (status: screening/qualified)
            const acceptedStatuses = ['screening'];
            const acceptedApps = applications.filter(app => 
                acceptedStatuses.includes(app.status?.toLowerCase())
            );
            
            // Calculate Rejected Applicants (status: rejected/disqualified)
            const rejectedApps = applications.filter(app => 
                app.status?.toLowerCase() === 'rejected'
            );
            
            // Calculate Hires (status: hired, offer_accepted)
            const hiredStatuses = ['hired', 'offer_accepted'];
            const hiredApps = applications.filter(app => 
                hiredStatuses.includes(app.status?.toLowerCase())
            );
            
            // Calculate Job Match Percentage (accepted + hired / total applications)
            const matchedApps = acceptedApps.length + hiredApps.length;
            let jobMatchPercentage = 0;
            if (applications.length > 0) {
                // Calculate percentage accurately: (matched / total) * 100
                const rawPercentage = (matchedApps / applications.length) * 100;
                jobMatchPercentage = Math.round(rawPercentage);
                // Ensure percentage is between 0 and 100
                jobMatchPercentage = Math.max(0, Math.min(100, jobMatchPercentage));
            }
            
            setSummaryStats({
                totalJobsListed,
                candidateMatches,
                acceptedApplicants: acceptedApps.length,
                rejectedApplicants: rejectedApps.length,
                hires: hiredApps.length,
                candidateMatchesChange: 0,
                acceptedApplicantsChange: 0,
                rejectedApplicantsChange: 0,
                hiresChange: 0,
                candidateMatchesLastWeek: 0,
                acceptedApplicantsLastWeek: 0,
                rejectedApplicantsLastWeek: 0,
                hiresLastWeek: 0,
                jobMatchPercentage,
                jobMatchChange: 0
            });
        } catch (error) {
            // Error fetching summary stats
        } finally {
            setStatsLoading(false);
        }
    };

    // Helper function to get flag emoji based on location
    const getLocationFlag = (location) => {
        if (!location) return '🌍';
        
        const locationLower = location.toLowerCase();
        
        // Country/region mappings
        const flagMap = {
            'singapore': '🇸🇬',
            'malaysia': '🇲🇾',
            'thailand': '🇹🇭',
            'indonesia': '🇮🇩',
            'philippines': '🇵🇭',
            'vietnam': '🇻🇳',
            'cambodia': '🇰🇭',
            'laos': '🇱🇦',
            'myanmar': '🇲🇲',
            'brunei': '🇧🇳',
            'australia': '🇦🇺',
            'new zealand': '🇳🇿',
            'japan': '🇯🇵',
            'south korea': '🇰🇷',
            'china': '🇨🇳',
            'hong kong': '🇭🇰',
            'taiwan': '🇹🇼',
            'india': '🇮🇳',
            'bangladesh': '🇧🇩',
            'sri lanka': '🇱🇰',
            'pakistan': '🇵🇰',
            'united states': '🇺🇸',
            'usa': '🇺🇸',
            'canada': '🇨🇦',
            'united kingdom': '🇬🇧',
            'uk': '🇬🇧',
            'germany': '🇩🇪',
            'france': '🇫🇷',
            'italy': '🇮🇹',
            'spain': '🇪🇸',
            'netherlands': '🇳🇱',
            'switzerland': '🇨🇭',
            'sweden': '🇸🇪',
            'norway': '🇳🇴',
            'denmark': '🇩🇰',
            'finland': '🇫🇮',
            'ireland': '🇮🇪',
            'austria': '🇦🇹',
            'belgium': '🇧🇪',
            'portugal': '🇵🇹',
            'greece': '🇬🇷',
            'poland': '🇵🇱',
            'czech republic': '🇨🇿',
            'hungary': '🇭🇺',
            'romania': '🇷🇴',
            'bulgaria': '🇧🇬',
            'croatia': '🇭🇷',
            'slovenia': '🇸🇮',
            'slovakia': '🇸🇰',
            'estonia': '🇪🇪',
            'latvia': '🇱🇻',
            'lithuania': '🇱🇹',
            'russia': '🇷🇺',
            'ukraine': '🇺🇦',
            'turkey': '🇹🇷',
            'israel': '🇮🇱',
            'saudi arabia': '🇸🇦',
            'uae': '🇦🇪',
            'united arab emirates': '🇦🇪',
            'qatar': '🇶🇦',
            'kuwait': '🇰🇼',
            'bahrain': '🇧🇭',
            'oman': '🇴🇲',
            'jordan': '🇯🇴',
            'lebanon': '🇱🇧',
            'egypt': '🇪🇬',
            'south africa': '🇿🇦',
            'cape town': '🇿🇦',
            'johannesburg': '🇿🇦',
            'durban': '🇿🇦',
            'pretoria': '🇿🇦',
            'port elizabeth': '🇿🇦',
            'bloemfontein': '🇿🇦',
            'nigeria': '🇳🇬',
            'kenya': '🇰🇪',
            'ghana': '🇬🇭',
            'morocco': '🇲🇦',
            'tunisia': '🇹🇳',
            'algeria': '🇩🇿',
            'brazil': '🇧🇷',
            'argentina': '🇦🇷',
            'chile': '🇨🇱',
            'colombia': '🇨🇴',
            'peru': '🇵🇪',
            'mexico': '🇲🇽',
            'venezuela': '🇻🇪',
            'ecuador': '🇪🇨',
            'uruguay': '🇺🇾',
            'paraguay': '🇵🇾',
            'bolivia': '🇧🇴',
            'guyana': '🇬🇾',
            'suriname': '🇸🇷',
            'french guiana': '🇬🇫'
        };

        // Check for exact matches first
        if (flagMap[locationLower]) {
            return flagMap[locationLower];
        }

        // Check for partial matches (e.g., "New York, USA" should match "USA")
        for (const [country, flag] of Object.entries(flagMap)) {
            if (locationLower.includes(country)) {
                return flag;
            }
        }

        // Default to world flag if no match found
        return '🌍';
    };

    // Filter jobs based on selected tab and search query
    const filteredJobs = dynamicJobApplications
        .filter(job => {
            // Filter by tab
            // "All" tab shows only Posted jobs (excludes Draft and Archived)
            // Draft jobs only appear in "Draft" tab
            // Archived jobs only appear in "Archived" tab
            const tabMatch = selectedTab === "All" 
                ? job.status === "Posted"
                : (selectedTab === "Active" && job.status === "Posted") ||
                  (selectedTab === "Draft" && job.status === "Draft") ||
                  (selectedTab === "Archived" && job.status === "Archived");
            
            // Filter by search query
            const searchMatch = !searchQuery || 
                job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
                job.location.toLowerCase().includes(searchQuery.toLowerCase());
            
            return tabMatch && searchMatch;
        })
        .sort((a, b) => {
            // Sort by created_at date
            const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
            const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
            
            // Handle invalid dates
            if (isNaN(dateA.getTime())) return 1;
            if (isNaN(dateB.getTime())) return -1;
            
            if (sortOrder === "newest") {
                // Newest first (descending)
                return dateB.getTime() - dateA.getTime();
            } else {
                // Oldest first (ascending)
                return dateA.getTime() - dateB.getTime();
            }
        });

    // Clear selection when tab changes
    useEffect(() => {
        setSelectedJobs([]);
    }, [selectedTab]);

    // Group jobs by company (jobs are already sorted, so they maintain order within groups)
    const groupedJobs = filteredJobs.reduce((groups, job) => {
        const company = job.company;
        if (!groups[company]) {
            groups[company] = [];
        }
        groups[company].push(job);
        return groups;
    }, {});
    
    // Ensure jobs within each group maintain sort order
    Object.keys(groupedJobs).forEach(company => {
        groupedJobs[company] = [...groupedJobs[company]]; // Maintain order from filteredJobs
    });

    // Handle team member click to navigate to their profile
    const handleTeamMemberClick = (member) => {
        if (member.userProfileId) {
            navigate(`/candidate-profile/${member.userProfileId}`);
        }
    };

    // Handle close team member modal
    const handleCloseTeamMemberModal = () => {
        setShowTeamMemberModal(false);
        setTeamMemberForm({
            firstName: "",
            lastName: "",
            workEmail: "",
            role: "member"
        });
        setEditingMemberId(null);
    };

    // Open team member modal for adding
    const handleAddTeamMember = () => {
        if (hasReachedSeatLimit) {
            alert(
                `Your ${currentPlanLabel} plan includes ${teamSeatLimit} team seat${teamSeatLimit === 1 ? "" : "s"}. ` +
                `You have reached the limit. Remove someone or upgrade to add more.`
            );
            return;
        }
        setTeamMemberForm({
            firstName: "",
            lastName: "",
            workEmail: "",
            role: "member"
        });
        setEditingMemberId(null);
        setShowTeamMemberModal(true);
    };

    // Open team member modal for editing
    const handleEditTeamMember = (member) => {
        const roleValue = member.allRoles && member.allRoles.length > 0 
            ? member.allRoles[0].toLowerCase() 
            : (member.role || "member").toLowerCase();
        
        setTeamMemberForm({
            firstName: member.name.split(' ')[0] || "",
            lastName: member.name.split(' ').slice(1).join(' ') || "",
            workEmail: member.email || "",
            role: ['admin', 'member', 'read-only'].includes(roleValue) ? roleValue : 'member'
        });
        setEditingMemberId(member.id);
        setShowTeamMemberModal(true);
    };

    // Delete team member
    const handleDeleteTeamMember = async (memberId) => {
        if (!window.confirm("Are you sure you want to delete this team member?")) {
            return;
        }

        try {
            const { error } = await supabase
                .from('team_members')
                .delete()
                .eq('id', memberId)
                .eq('employer_id', user.id);

            if (error) throw error;

            // Refresh team members
            await fetchTeamMembers();
            alert("Team member deleted successfully");
        } catch (error) {
            alert('Failed to delete team member. Please try again.');
        }
    };

    // Save team member (add or update)
    const handleSaveTeamMember = async () => {
        if (!teamMemberForm.firstName || !teamMemberForm.workEmail) {
            alert("Please fill in required fields (First Name and Email)");
            return;
        }

        setSavingTeamMember(true);
        try {
            // Validate email against users_profiles
            let userProfileId = null;
            let userType = null;
            let profileImageUrl = null;
            let profileFirstName = null;
            let profileLastName = null;

            if (teamMemberForm.workEmail) {
                const { data: profileData, error: profileError } = await supabase
                    .from('users_profiles')
                    .select('profileid, email, role, profile_image_url, firstname, lastname')
                    .eq('email', teamMemberForm.workEmail.toLowerCase())
                    .single();

                if (!profileError && profileData) {
                    userProfileId = profileData.profileid;
                    userType = profileData.role;
                    profileImageUrl = profileData.profile_image_url;
                    profileFirstName = profileData.firstname;
                    profileLastName = profileData.lastname;
                }
            }

            const rolesArray = [teamMemberForm.role];

            if (editingMemberId) {
                // Update existing team member
                const { error } = await supabase
                    .from('team_members')
                    .update({
                        first_name: teamMemberForm.firstName,
                        last_name: teamMemberForm.lastName,
                        work_email: teamMemberForm.workEmail,
                        roles: rolesArray,
                        user_profile_id: userProfileId,
                        user_type: userType
                    })
                    .eq('id', editingMemberId)
                    .eq('employer_id', user.id);

                if (error) throw error;
                alert("Team member updated successfully");
            } else {
                // Backend validation: Check existing team members count before adding
                const { count, error: countError } = await supabase
                    .from('team_members')
                    .select('id', { count: 'exact' })
                    .eq('employer_id', user.id);

                if (countError) throw countError;

                const existingCount = count || 0;
                if (existingCount >= teamSeatLimit) {
                    alert(
                        `Your ${currentPlanLabel} plan allows ${teamSeatLimit} team seat${teamSeatLimit === 1 ? "" : "s"}. ` +
                        `You already have ${existingCount} team member${existingCount === 1 ? "" : "s"}. ` +
                        `Please remove someone or upgrade your plan to add more.`
                    );
                    return;
                }

                // Add new team member
                const { error } = await supabase
                    .from('team_members')
                    .insert({
                        employer_id: user.id,
                        first_name: teamMemberForm.firstName,
                        last_name: teamMemberForm.lastName,
                        work_email: teamMemberForm.workEmail,
                        roles: rolesArray,
                        user_profile_id: userProfileId,
                        user_type: userType
                    });

                if (error) throw error;
                alert("Team member added successfully");
                
                // Send team invite email (only for new members, not when editing)
                try {
                    const inviterName = `${profileInfo?.firstname || ''} ${profileInfo?.lastname || ''}`.trim() || user?.email?.split('@')[0] || 'Team Admin';
                    // Get company name from user metadata or profile
                    const companyName = user?.user_metadata?.company_name || profileInfo?.company_name || 'your company';
                    const inviteeName = `${teamMemberForm.firstName} ${teamMemberForm.lastName || ''}`.trim() || teamMemberForm.workEmail.split('@')[0];
                    const roleLabel = teamMemberForm.role === 'admin' ? 'Admin' : teamMemberForm.role === 'read-only' ? 'Read-Only' : 'Member';
                    // Generate invite link - you may want to create a proper invite token system later
                    const inviteLink = `https://doctorsjobs.io/signup?invite=${encodeURIComponent(teamMemberForm.workEmail)}`;
                    
                    await sendTeamInviteEmail(
                        teamMemberForm.workEmail,
                        inviteeName,
                        inviterName,
                        companyName,
                        roleLabel,
                        inviteLink
                    );
                } catch (emailError) {
                    // Don't fail the invite if email fails
                }
            }

            // Refresh team members
            await fetchTeamMembers();
            setShowTeamMemberModal(false);
            setTeamMemberForm({
                firstName: "",
                lastName: "",
                workEmail: "",
                role: "member"
            });
            setEditingMemberId(null);
        } catch (error) {
            alert(`Failed to save team member: ${error.message || 'Please try again.'}`);
        } finally {
            setSavingTeamMember(false);
        }
    };

    // Handle job selection
    const handleSelectJob = (jobId) => {
        setSelectedJobs(prev => 
            prev.includes(jobId) 
                ? prev.filter(id => id !== jobId)
                : [...prev, jobId]
        );
    };

    // Handle select all jobs
    const handleSelectAll = () => {
        if (selectedJobs.length === filteredJobs.length && filteredJobs.length > 0) {
            setSelectedJobs([]);
        } else {
            setSelectedJobs(filteredJobs.map(job => job.id));
        }
    };

    // Update job status in bulk
    const updateJobStatus = async (newStatus) => {
        if (selectedJobs.length === 0) return;

            // Map UI status to database status
        const statusMap = {
            'Posted': 'published',
            'Active': 'published',
            'Draft': 'draft',
            'Archived': 'closed'
        };
        const dbStatus = statusMap[newStatus] || (newStatus === 'Posted' ? 'published' : newStatus.toLowerCase());

        try {
            const { error } = await supabase
                .from('internal_job_listings')
                .update({ status: dbStatus })
                .in('id', selectedJobs)
                .eq('employer_id', user.id);

            if (error) throw error;

            // Update local state
            setDynamicJobApplications(prev => 
                prev.map(job => {
                    if (selectedJobs.includes(job.id)) {
                        const statusMap = {
                            'published': 'Posted',
                            'draft': 'Draft',
                            'closed': 'Archived'
                        };
                        return { ...job, status: statusMap[dbStatus] || job.status };
                    }
                    return job;
                })
            );

            // Clear selection
            setSelectedJobs([]);
            
            // Switch to the appropriate tab based on the new status
            if (newStatus === 'Archived') {
                setSelectedTab('Archived');
            } else if (newStatus === 'Draft') {
                setSelectedTab('Draft');
            } else if (newStatus === 'Posted') {
                setSelectedTab('Active');
            }
            
            // Refresh job listings to get updated data
            await fetchJobListings();
            
            // Show success message
            alert(`Successfully moved ${selectedJobs.length} job(s) to ${newStatus}`);
        } catch (error) {
            alert(`Failed to update job status: ${error.message || 'Please try again.'}`);
        }
    };

    // Compute summary cards from dynamic data
    const summaryCards = [
        {
            title: "Total Jobs Listed",
            value: statsLoading ? "..." : summaryStats.totalJobsListed.toString(),
            icon: "/Job Matches.svg",
        },
        {
            title: "Total Applicants",
            value: statsLoading ? "..." : summaryStats.candidateMatches.toString(),
            icon: "/Total Applications.svg",
        },
        {
            title: "Total Hires",
            value: statsLoading ? "..." : summaryStats.hires.toString(),
            icon: "/Total Interviews.svg",
        },
        {
            title: "Rejected Applicants",
            value: statsLoading ? "..." : summaryStats.rejectedApplicants.toString(),
            icon: "/Total Rejected Applications.svg",
        }
    ];

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .employer-dashboard-container {
                    padding: 1rem !important;
                }
                .employer-dashboard-kpi-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                    gap: 0.75rem !important;
                }
                .employer-dashboard-card {
                    padding: 1rem !important;
                }
                .employer-dashboard-card h3 {
                    font-size: 0.875rem !important;
                }
                .employer-dashboard-card .text-2xl {
                    font-size: 1.5rem !important;
                }
                .employer-dashboard-main-grid {
                    grid-template-columns: 1fr !important;
                    gap: 1rem !important;
                }
                .employer-dashboard-left {
                    padding: 1rem !important;
                }
                .employer-dashboard-section-header {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.5rem !important;
                }
                .employer-dashboard-controls {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 0.75rem !important;
                }
                .employer-dashboard-tabs {
                    width: 100% !important;
                    overflow-x: auto !important;
                }
                .employer-dashboard-tabs button {
                    font-size: 0.75rem !important;
                    padding-bottom: 0.25rem !important;
                }
                .employer-dashboard-filters {
                    width: 100% !important;
                    flex-direction: column !important;
                    gap: 0.5rem !important;
                }
                .employer-dashboard-filter-button {
                    display: none !important;
                }
                .employer-dashboard-filters input {
                    width: 100% !important;
                    padding: 0.5rem 0.5rem 0.5rem 1.75rem !important;
                    font-size: 0.75rem !important;
                }
                .employer-dashboard-bulk-actions {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.5rem !important;
                }
                .employer-dashboard-table {
                    display: none !important;
                }
                .employer-dashboard-card-list {
                    display: block !important;
                }
                .employer-dashboard-mobile-card {
                    padding: 1rem !important;
                }
                .employer-dashboard-bottom-grid {
                    grid-template-columns: 1fr !important;
                    gap: 1rem !important;
                }
                .employer-dashboard-stats-grid {
                    grid-template-columns: 1fr !important;
                    gap: 0.75rem !important;
                }
                .employer-dashboard-analytics {
                    padding: 1rem !important;
                }
                .employer-dashboard-analytics h3 {
                    font-size: 0.875rem !important;
                }
            }
        `}</style>
        <div className="max-w-7xl mx-auto p-6 bg-white min-h-screen employer-dashboard-container">
            {/* Top KPI Cards */}
            <Suspense fallback={<div className="flex justify-center items-center py-8"><LottieLoader size="64px" /></div>}>
                <DashboardStats
                    summaryCards={summaryCards}
                    statsLoading={statsLoading}
                />
            </Suspense>

            {/* Middle Section - Split Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 employer-dashboard-main-grid">
                {/* Left Half - Your Jobs */}
                <Suspense fallback={<div className="flex justify-center items-center py-8"><LottieLoader size="64px" /></div>}>
                    <YourJobsSection
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        sortOrder={sortOrder}
                        setSortOrder={setSortOrder}
                        showSortMenu={showSortMenu}
                        setShowSortMenu={setShowSortMenu}
                        jobsLoading={jobsLoading}
                        groupedJobs={groupedJobs}
                        groupExpanded={groupExpanded}
                        setGroupExpanded={setGroupExpanded}
                        filteredJobs={filteredJobs}
                        selectedJobs={selectedJobs}
                        handleSelectJob={handleSelectJob}
                        handleSelectAll={handleSelectAll}
                        updateJobStatus={updateJobStatus}
                        setSelectedJobs={setSelectedJobs}
                    />
                </Suspense>

                {/* Right Half - Analytics and Team Members */}
                <div className="lg:col-span-1 space-y-4 mt-6 lg:mt-0">
                    <Suspense fallback={<div className="flex justify-center items-center py-8"><LottieLoader size="64px" /></div>}>
                        {/* TODO: do not delete -  will be fixed and put back */}
                        {/* <DashboardAnalytics
                            summaryStats={summaryStats}
                            statsLoading={statsLoading}
                            profileAnalytics={profileAnalytics}
                        /> */}
                        <TeamMembersCard
                            teamMembers={teamMembers}
                            loading={loading}
                            creditsLoading={creditsLoading}
                            currentTeamCount={currentTeamCount}
                            teamSeatLimit={teamSeatLimit}
                            currentPlanLabel={currentPlanLabel}
                            hasReachedSeatLimit={hasReachedSeatLimit}
                            handleAddTeamMember={handleAddTeamMember}
                            handleTeamMemberClick={handleTeamMemberClick}
                            handleEditTeamMember={handleEditTeamMember}
                            handleDeleteTeamMember={handleDeleteTeamMember}
                        />
                    </Suspense>
                </div>
            </div>

            {/* Team Member Modal */}
            <Suspense fallback={null}>
                <TeamMemberModal
                    showTeamMemberModal={showTeamMemberModal}
                    editingMemberId={editingMemberId}
                    teamMemberForm={teamMemberForm}
                    setTeamMemberForm={setTeamMemberForm}
                    savingTeamMember={savingTeamMember}
                    creditsLoading={creditsLoading}
                    currentTeamCount={currentTeamCount}
                    teamSeatLimit={teamSeatLimit}
                    currentPlanLabel={currentPlanLabel}
                    hasReachedSeatLimit={hasReachedSeatLimit}
                    handleCloseModal={handleCloseTeamMemberModal}
                    handleSaveTeamMember={handleSaveTeamMember}
                />
            </Suspense>
        </div>
        </>
    );
};

export default EmployerDashboard;


